/* Efeitos sonoros sintetizados (WebAudio) — sem download, tocam na hora. */
let ctx = null;
const getCtx = () => {
  if (typeof window === 'undefined') return null;
  if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  return ctx;
};

export const wbSoundOn = () => localStorage.getItem('wb_sound') !== 'off';
export const setWbSound = (on) => localStorage.setItem('wb_sound', on ? 'on' : 'off');

const vol = (c, v, t0, dur) => {
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(v, t0 + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  g.connect(c.destination);
  return g;
};

const tone = (c, { f0, f1 = f0, type = 'sine', v = 0.2, dur = 0.2, at = 0 }) => {
  const t0 = c.currentTime + at;
  const o = c.createOscillator();
  o.type = type;
  o.frequency.setValueAtTime(f0, t0);
  o.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t0 + dur);
  o.connect(vol(c, v, t0, dur));
  o.start(t0); o.stop(t0 + dur + 0.02);
};

const noise = (c, { v = 0.3, dur = 0.5, at = 0, lp = 1200 }) => {
  const t0 = c.currentTime + at;
  const len = Math.floor(c.sampleRate * dur);
  const buf = c.createBuffer(1, len, c.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / len);
  const s = c.createBufferSource();
  s.buffer = buf;
  const f = c.createBiquadFilter();
  f.type = 'lowpass'; f.frequency.setValueAtTime(lp, t0);
  f.frequency.exponentialRampToValueAtTime(80, t0 + dur);
  s.connect(f); f.connect(vol(c, v, t0, dur));
  s.start(t0);
};

const play = (fn) => {
  if (!wbSoundOn()) return null;
  try { const c = getCtx(); return c ? fn(c) : null; } catch (e) { return null; }
};

/* Clipes reais (WebAudio, já destravado pelo toque): estouro = o MESMO da GB Arena; inflar = áudios enviados pelo usuário.
   Enquanto um clipe não carregou (ou falhou), cai no som sintetizado equivalente. */
const CLIP = { boom: '/gb/sfx/estouro.mp3', inflateShort: '/wb/sfx/inflate_2.mp3', inflateLong: '/wb/sfx/inflate_4.mp3' };
const clips = {};
const loadClip = (url) => {
  if (clips[url] || typeof window === 'undefined') return;
  const c = getCtx();
  if (!c) return;
  clips[url] = { buf: null };
  fetch(url).then((r) => r.arrayBuffer()).then((ab) => c.decodeAudioData(ab))
    .then((buf) => { clips[url].buf = buf; }).catch(() => { delete clips[url]; });
};
const playClip = (c, url, { vol = 1, rate = 1, fallback } = {}) => {
  const e = clips[url];
  if (!e || !e.buf) { loadClip(url); if (fallback) fallback(c); return null; }
  const src = c.createBufferSource(); src.buffer = e.buf; src.playbackRate.value = rate;
  const g = c.createGain(); g.gain.value = vol;
  src.connect(g); g.connect(c.destination);
  src.start();
  return src;
};
export const preloadSfx = () => Object.values(CLIP).forEach(loadClip);
export const preloadBoom = preloadSfx;

const boomSynth = (c) => {
  noise(c, { v: 0.9, dur: 1.4, lp: 2400 });
  tone(c, { f0: 120, f1: 30, type: 'sawtooth', v: 0.4, dur: 1.1 });
  tone(c, { f0: 900, f1: 100, type: 'square', v: 0.12, dur: 0.3, at: 0.02 });
};
const pumpSynth = (c) => { noise(c, { v: 0.18, dur: 0.45, lp: 900 }); tone(c, { f0: 140, f1: 260, type: 'triangle', v: 0.12, dur: 0.5 }); };
const boom = () => play((c) => playClip(c, CLIP.boom, { vol: 0.95, fallback: boomSynth }));
let pumpSrc = null;
/* Balão inflando: clipe curto para subidas pequenas, longo para recargas grandes; o tom sobe junto com a pressão. */
const pump = (delta = 0, to = 0) => {
  try { if (pumpSrc) pumpSrc.stop(); } catch (e) { /* já parou */ }
  const rate = 0.95 + Math.min(100, Math.max(0, Number(to) || 0)) / 100 * 0.25;
  pumpSrc = play((c) => playClip(c, Math.abs(delta) >= 6 ? CLIP.inflateLong : CLIP.inflateShort, { vol: 0.85, rate, fallback: pumpSynth }));
};

export const wbSfx = {
  click: () => play((c) => tone(c, { f0: 600, f1: 300, type: 'square', v: 0.08, dur: 0.08 })),
  pump,
  tremble: () => play((c) => tone(c, { f0: 70, f1: 50, type: 'sawtooth', v: 0.1, dur: 0.6 })),
  explosion: boom,
  candy: () => play((c) => { tone(c, { f0: 880, f1: 1320, v: 0.14, dur: 0.12 }); tone(c, { f0: 1320, f1: 1760, v: 0.12, dur: 0.16, at: 0.09 }); }),
  coin: () => play((c) => { [1046, 1318, 1568, 2093].forEach((f, i) => tone(c, { f0: f, type: 'triangle', v: 0.16, dur: 0.22, at: i * 0.07 })); }),
  unlock: () => play((c) => { [523, 659, 784, 1046].forEach((f, i) => tone(c, { f0: f, type: 'sine', v: 0.18, dur: 0.35, at: i * 0.1 })); }),
  whoosh: () => play((c) => noise(c, { v: 0.2, dur: 0.35, lp: 3000 })),
  empty: () => play((c) => tone(c, { f0: 300, f1: 150, type: 'triangle', v: 0.12, dur: 0.35 })),
};
